import { Logger } from '../../utilities/Logger.js'
import { isTouchDevice, desktopMedia } from '../../utilities/device-detection'

const LARGE_BREAKPOINT = 1280

export class SliderCardSet {
  constructor (elem) {
    this.wrapper = elem.querySelector('[data-slider-card-set-wrapper]')
    this.pagination = elem.querySelector('[data-slider-card-set-pagination]')
    this.prevBtn = elem.querySelector('[data-slider-card-set-prev]')
    this.nextBtn = elem.querySelector('[data-slider-card-set-next]')
    this.cards = elem.querySelectorAll('[data-slider-card-set-item]')
    this.isFullWidth = elem.hasAttribute('data-slider-card-set-full-width')
    this.currentFocus = 0
    this.adjustNavigationButtons = this.adjustNavigationButtons.bind(this)
    this.logger = new Logger('Slider card set')

    if (!this.wrapper || !this.cards?.length) {
      return
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.setButtonStates()
        const currentFocus = this.getCurrentFocusBasedOnScroll()
        // Update card index.

        if (currentFocus > 0) {
          const targetCardLeft =
            this.cards[currentFocus - 1].offsetLeft - this.parentPaddingLeft - this.wrapper.offsetLeft

          this.wrapper.scrollTo({
            left: targetCardLeft,
            behavior: 'smooth'
          })

          this.cards[currentFocus - 1].focus()
          this.announceCardChange(this.cards[currentFocus - 1])
        } else {
          const zeroLeft = this.cards[0].offsetLeft - this.parentPaddingLeft - this.wrapper.offsetLeft
          this.wrapper.scrollTo({
            left: zeroLeft,
            behavior: 'smooth'
          })
        }
      })
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.setButtonStates()

        const currentFocus = this.getCurrentFocusBasedOnScroll()

        if (currentFocus < this.cards.length - 1) {
          const targetCardLeft =
            this.cards[currentFocus + 1].offsetLeft - this.parentPaddingLeft - this.wrapper.offsetLeft

          this.wrapper.scrollTo({
            left: targetCardLeft,
            behavior: 'smooth'
          })

          this.cards[currentFocus + 1].focus()
          this.announceCardChange(this.cards[currentFocus + 1])
        }
      })
    }

    this.wrapper.addEventListener('scrollend', (e) => {
      this.setButtonStates()
    })

    this.adjustNavigationButtons()

    window.addEventListener('resize', this.adjustNavigationButtons)
  }

  announceCardChange (card) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'alert')
    announcement.setAttribute('aria-live', 'polite')
    announcement.classList.add('visually-hidden')
    announcement.innerText = `Viewing ${card.getAttribute('aria-label')}`
    document.body.appendChild(announcement)

    // Remove the announcement after it has been read
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)

    setTimeout(() => {
      this.setButtonStates()
    }, 200)
  }

  setButtonStates () {
    this.setButtonState(this.prevBtn, this.wrapper.scrollLeft <= 10)
    this.setButtonState(this.nextBtn, this.wrapper.scrollWidth - this.wrapper.scrollLeft <= (this.wrapper.clientWidth + 10))
  }

  setButtonState (button, state) {
    button.disabled = state
  }

  adjustNavigationButtons () {
    const viewportWidth = window.innerWidth

    if (!this.isFullWidth && viewportWidth >= LARGE_BREAKPOINT) {
      this.pagination.style.display = 'none'
      return // exit the function early
    }

    const cardWidth = this.cards[0].offsetWidth
    const cardMinWidth = parseFloat(
      getComputedStyle(this.cards[0]).getPropertyValue('flex-basis')
    )

    // Check the width against min-width and show/hide navigation buttons
    if (cardWidth > cardMinWidth || (isTouchDevice() && !desktopMedia.matches)) {
      this.pagination.style.display = 'none'
    } else {
      this.pagination.style.display = 'flex'
    }
  }

  getCurrentFocusBasedOnScroll () {
    const scrollPosition = this.wrapper.scrollLeft
    this.parentPaddingLeft = parseFloat(
      getComputedStyle(this.wrapper).getPropertyValue('padding-left')
    )

    const closestCardIndex = 0
    const minDiff = Math.round(scrollPosition + this.wrapper.offsetLeft)

    // First card on screen.
    for (let index = 0; index < this.cards.length; index++) {
      const diff = Math.round(this.cards[index].offsetLeft - this.parentPaddingLeft)
      if (diff >= minDiff) {
        return index
      }
    }

    return closestCardIndex
  }
}

export function setupSliderCardSets (document) {
  const elems = document.querySelectorAll('[data-slider-card-set]')
  const sliderCardSets = []

  for (const element of elems) {
    sliderCardSets.push(new SliderCardSet(element))
  }

  return sliderCardSets
}
